import Request from 'src/utils/request'

/**
 * @typedef GetWxWorkUserInfoApiResponse 在企业微信单聊中获取用户信息（根据externalUserId）Api返回值
 * @property {{ accountId: number, accountName: string, followStaffList: { changeTime: string, isFollow: number, staffName: string }[], isSubscribe: number, sourceDescription: string, subscribeTime: string, unsubscribeTime: string }[]} accountList 关联微信平台账号
 * @property {number} defaultPatientId 默认患者ID（用户无患者时返回0）
 * @property {number} patientNum 患者数量
 * @property {string} registerTime 注册时间
 * @property {number} userId 用户ID
 * @property {string} userNick 用户名称
 * @property {string} userSource 用户来源
 */

/**
 * 在企业微信单聊中获取用户信息（根据externalUserId）
 * @param {string} externalUserId 企业微信用户ID
 * @returns {Promise<GetWxWorkUserInfoApiResponse>}
 */
export function getWxWorkUserInfoApi (externalUserId) {
  return Request({
    url: `/follow/wx/work/user/info/2/${externalUserId}`,
    method: 'get'
  })
}

/**
 * @typedef GetWxWorkPatientListApiResponse 获取企业微信用户的患者列表Api返回值
 * @property {string} contactPhone 联系电话
 * @property {number} patientAge 患者年龄
 * @property {number} patientId 患者ID
 * @property {string} patientName 患者姓名
 * @property {number} patientSex 患者性别（1:男 2:女）
 * @property {string} userRelationship 用户与患者关系
 */

/**
 * 获取企业微信用户的患者列表
 * @param {number} userId 用户ID
 * @returns {Promise<GetWxWorkPatientListApiResponse[]>}
*/
export function getWxWorkPatientListApi (userId) {
  return Request({
    url: `/follow/patient/patient/list/${userId}`,
    method: 'get'
  })
}

export function getWxWorkPatientOverviewApi (patientId) {
  return Request({
    url: `/follow/patientAccount/getCardPacks?patientId=${patientId}`,
    method: 'get'
  })
}

export function getWxWorkPatientOverviewOldApi (patientId) {
  return Request({
    url: `/follow-up/api/v1/patient/${patientId}/overview`,
    method: 'get'
  })
}

/**
 * 患者所有组列表包含所有组内工单
 */
export function getPatientManageGroupListApi (params) {
  return Request({
    url: '/api/v1/patient/manager/group/list',
    method: 'get',
    domainType: 'follow-up',
    params
  })
}

/**
 * @typedef GetWxWorkPatientInfoApiResponse 在企业微信中获取患者信息Api返回值
 * @property {{ diagnoseTime: string, doctorName: string, hospitalName: string, latestDiagnosis: string }} latestDiagnoseInfo 最新诊断信息
 * @property {{ adviceTime: string, doctorName: string, hospitalName: string, latestAdvice: { status: string, title: string, visitTime: string }[] }} latestDoctorAdviceInfo 最新医嘱信息
 * @property {{ endTime: string, groupName: string, recipientName: string, servingPackageId: number, startTime: string }} managementGroup 管理组
 * @property {{ commercialInsuranceCondition: string, contactPhone: string, domicileAddress: string, patientAge: number, patientId: number, patientName: string, patientSex: number, socialInsuranceCondition: string, userRelationship: string }} patientBaseInfo 患者基本信息
 * @property {{ platformCommodityName: string, purchaseDate: string, realAmount: number }[]} platformCommodityOrderList 平台商品购买记录（福利计划）
 */

/**
 * 在企业微信中获取患者信息
 * @param {number} patientId 患者ID
 * @returns {Promise<GetWxWorkPatientInfoApiResponse>}
 */
/* export function getWxWorkPatientInfoApi (patientId) {
  return Request({
    url: `/wx/work/${patientId}/patient/info`,
    method: 'get'
  })
} */
export function getWxWorkPatientInfoApi (patientId, params) {
  return Request({
    url: `/follow/patient/info/${patientId}`,
    method: 'get',
    params
  })
}

/**
 * @typedef PostWxWorkGroupChatInfoApiResponse 获取企业微信群聊信息Api返回值
 * @property {number} userCount 用户数
 * @property {{ inServiceOrderNum: number, latestPatientName: string, totalOrderNum: number, userAvatar: string, userId: number, userName: string }[]} wxWorkGroupChatUserInfoList 企业微信群聊用户信息列表
 */

/**
 * 获取企业微信群聊信息
 * @param {Object} data 请求所需参数
 * @param {number} data.agentId 应用ID
 * @param {string} data.chatId 群聊ID
 * @returns {Promise<PostWxWorkGroupChatInfoApiResponse>}
*/
export function postWxWorkGroupChatInfoApi (data) {
  return Request({
    url: '/wx/work/group/chat/info',
    method: 'post',
    data
  })
}

/**
 * @typedef GetWxWorkGroupChatUserInfoApiResponse 在企业微信群聊中获取用户信息（根据userId）Api返回值
 * @property {{ accountId: number, accountName: string, followStaffList: { changeTime: string, isFollow: number, staffName: string }[], isSubscribe: number, sourceDescription: string, subscribeTime: string, unsubscribeTime: string }[]} accountList 关联微信平台账号
 * @property {number} defaultPatientId 默认患者ID（用户无患者时返回0）
 * @property {number} patientNum 患者数量
 * @property {string} registerTime 注册时间
 * @property {number} userId 用户ID
 * @property {string} userNick 用户名称
 * @property {string} userSource 用户来源
 */

/**
 * 在企业微信群聊中获取用户信息（根据userId）
 * @param {number} userId 用户ID
 * @returns {Promise<GetWxWorkGroupChatUserInfoApiResponse>}
 */
export function getWxWorkGroupChatUserInfoApi (userId) {
  return Request({
    url: `/wx/work/group/chat/${userId}/user/info`,
    method: 'get'
  })
}
/**
 * @typedef GetReminderListApiResponse 获取备忘录列表Api返回值
 * @property {string} createTime 创建时间
 * @property {string} creatorName 创建人
 * @property {string} modifierName 修改人
 * @property {string} modifyTime 更新日期
 * @property {number} reminderId 备忘录id
 * @property {string} reminderTitle 备忘录标题
 */

/**
 * 获取备忘录列表
 * @param {number} patientId patientId
 * @returns {Promise<GetReminderListApiResponse[]>}
*/
export function getReminderListApi (patientId) {
  return Request({
    url: `/follow/patient/reminder/${patientId}/list`,
    method: 'get'
  })
}
/**
 * @typedef GetReminderDetailApiResponse 获取备忘录详情Api返回值
 * @property {number} creator 创建人ID（修改时校验）
 * @property {number} reminderId 备忘录id
 * @property {string} reminderText 备忘录内容
 * @property {string} reminderTitle 备忘录标题
 */

/**
 * 获取备忘录详情
 * @param {number} reminderId reminderId
 * @returns {Promise<GetReminderDetailApiResponse>}
*/
export function getReminderDetailApi (reminderId) {
  return Request({
    url: `/reminder/${reminderId}/detail`,
    method: 'get'
  })
}

export function postReachRecordVoidFlagApi (data) {
  return Request({
    url: '/follow/reachRecord/voidFlag',
    method: 'post',
    data
  })
}

export function postReachRecordSaveApi (data) {
  return Request({
    url: '/follow/reachRecord/save',
    method: 'post',
    data
  })
}
